import { defineSseEvent } from '@terra/shared-sse-contracts';
import { z } from 'zod';
import { InteractionSchema } from './interact.schema';

export const InteractSseEventTypeEnum = {
  INTERACTION_RECEIVED: 'INTERACTION_RECEIVED',
  SESSION_STARTED: 'SESSION_STARTED',
  SESSION_STOPPED: 'SESSION_STOPPED',
} as const;

export const StreamingSseEventTypeEnum = {
  MESSAGE_RECEIVED: 'MESSAGE_RECEIVED',
  CONNECTION_OPENED: 'CONNECTION_OPENED',
  CONNECTION_CLOSED: 'CONNECTION_CLOSED',
  CONNECTION_ERROR: 'CONNECTION_ERROR',
} as const;

export const InteractionReceivedPayloadSchema = z.object({
  sessionId: z.string(),
  interaction: InteractionSchema,
});

export const SessionStartedPayloadSchema = z.object({
  sessionId: z.string(),
  domain: z.string(),
  expiresAt: z.string(),
});

export const SessionStoppedPayloadSchema = z.object({
  sessionId: z.string(),
});

export const StreamingMessageSchema = z.object({
  id: z.string(),
  data: z.string(),
  eventType: z.string().optional(),
  timestamp: z.string(),
  direction: z.enum(['inbound', 'outbound']).optional(),
  encoding: z.enum(['utf8', 'base64']).optional(),
});

export const StreamingBasePayloadSchema = z.object({
  sessionId: z.string(),
  connectionType: z.union([z.literal('sse'), z.literal('websocket')]),
});

export const StreamingMessageReceivedPayloadSchema =
  StreamingBasePayloadSchema.extend({
    message: StreamingMessageSchema,
  });

export const StreamingConnectionOpenedPayloadSchema =
  StreamingBasePayloadSchema.extend({
    url: z.string(),
    connectedAt: z.string(),
  });

export const StreamingConnectionClosedPayloadSchema =
  StreamingBasePayloadSchema.extend({
    reason: z.string().optional(),
  });

export const StreamingConnectionErrorPayloadSchema =
  StreamingBasePayloadSchema.extend({
    error: z.string(),
  });

export const INTERACTION_RECEIVED_SSE_EVENT = defineSseEvent({
  type: InteractSseEventTypeEnum.INTERACTION_RECEIVED,
  payload: InteractionReceivedPayloadSchema,
  channelPrefix: 'interact',
  channelKey: ({ sessionId }) => sessionId,
});

export const SESSION_STARTED_SSE_EVENT = defineSseEvent({
  type: InteractSseEventTypeEnum.SESSION_STARTED,
  payload: SessionStartedPayloadSchema,
  channelPrefix: 'interact',
  channelKey: ({ sessionId }) => sessionId,
});

export const SESSION_STOPPED_SSE_EVENT = defineSseEvent({
  type: InteractSseEventTypeEnum.SESSION_STOPPED,
  payload: SessionStoppedPayloadSchema,
  channelPrefix: 'interact',
  channelKey: ({ sessionId }) => sessionId,
});

export const STREAMING_MESSAGE_RECEIVED_SSE_EVENT = defineSseEvent({
  type: StreamingSseEventTypeEnum.MESSAGE_RECEIVED,
  payload: StreamingMessageReceivedPayloadSchema,
  channelPrefix: 'streaming',
  channelKey: ({ sessionId }) => sessionId,
});

export const STREAMING_CONNECTION_OPENED_SSE_EVENT = defineSseEvent({
  type: StreamingSseEventTypeEnum.CONNECTION_OPENED,
  payload: StreamingConnectionOpenedPayloadSchema,
  channelPrefix: 'streaming',
  channelKey: ({ sessionId }) => sessionId,
});

export const STREAMING_CONNECTION_CLOSED_SSE_EVENT = defineSseEvent({
  type: StreamingSseEventTypeEnum.CONNECTION_CLOSED,
  payload: StreamingConnectionClosedPayloadSchema,
  channelPrefix: 'streaming',
  channelKey: ({ sessionId }) => sessionId,
});

export const STREAMING_CONNECTION_ERROR_SSE_EVENT = defineSseEvent({
  type: StreamingSseEventTypeEnum.CONNECTION_ERROR,
  payload: StreamingConnectionErrorPayloadSchema,
  channelPrefix: 'streaming',
  channelKey: ({ sessionId }) => sessionId,
});

export type InteractionReceivedPayload = z.infer<
  typeof InteractionReceivedPayloadSchema
>;
export type SessionStartedPayload = z.infer<typeof SessionStartedPayloadSchema>;
export type SessionStoppedPayload = z.infer<typeof SessionStoppedPayloadSchema>;
export type StreamingMessage = z.infer<typeof StreamingMessageSchema>;
export type StreamingMessageReceivedPayload = z.infer<
  typeof StreamingMessageReceivedPayloadSchema
>;
export type StreamingConnectionOpenedPayload = z.infer<
  typeof StreamingConnectionOpenedPayloadSchema
>;
export type StreamingConnectionClosedPayload = z.infer<
  typeof StreamingConnectionClosedPayloadSchema
>;
export type StreamingConnectionErrorPayload = z.infer<
  typeof StreamingConnectionErrorPayloadSchema
>;
